/**
 * App Providers
 *
 * Centralized provider composition for the Benefits Outreach 360 app.
 * Wraps all global providers in the correct order.
 *
 * Provider order (outer to inner):
 * 1. QueryClientProvider - TanStack Query for server state
 * 2. ReactQueryDevtools - Dev tools (development only)
 *
 * Future providers can be added here:
 * - ErrorBoundary (already in index.tsx)
 * - ThemeProvider
 * - AuthProvider
 * - etc.
 *
 * @example
 * ```tsx
 * // index.tsx
 * import { Providers } from './app/providers';
 *
 * root.render(
 *   <StrictMode>
 *     <Providers>
 *       <RouterProvider router={router} />
 *     </Providers>
 *   </StrictMode>
 * );
 * ```
 */

import React, { useEffect } from 'react';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { queryClient } from '@shared/lib/queryClient';
import { ToastContainer } from '@shared/components/ui/Toast';
import { FeatureFlagPanel } from '@shared/components/dev/FeatureFlagPanel';
import { initKanbanFromCloud } from '@features/campaigns/store/kanbanStore';
import { initTableColumnsFromCloud } from '@shared/store/tableColumnStore';
import { initEPGViewFromCloud } from '@/features/epg/stores';
import { initUIFromCloud } from '@shared/store/uiStore';
import { useEPGStoreCloudSync } from '@shared/store/epgStore';
import { useTableColumnStoreCloudSync } from '@shared/store/tableColumnStore';
import { useUIStoreCloudSync } from '@shared/store/uiStore';
import { logger } from '@shared/utils/logger';
import { usePostcardSSENotifications } from '@shared/hooks/usePostcardSSENotifications';

interface ProvidersProps {
  children: React.ReactNode;
}

/**
 * Check if user is authenticated.
 *
 * CRITICAL: This prevents cloud sync from running before login,
 * which would trigger 401 errors and infinite token refresh loops.
 */
const isAuthenticated = (): boolean => {
  if (typeof window === 'undefined') return false;

  const authData = localStorage.getItem('dms-auth-storage');
  if (authData) {
    try {
      const parsed = JSON.parse(authData);
      return parsed.state?.isAuthenticated === true;
    } catch {
      return false;
    }
  }
  return false;
};

/**
 * Root providers wrapper component
 *
 * Provides:
 * - TanStack Query context for data fetching
 * - DevTools panel in development mode
 * - Cloud sync initialization for Zustand stores
 */
export const Providers: React.FC<ProvidersProps> = ({ children }) => {
  // Debug: Log when providers mount
  console.log('[Providers] Mounting...');

  // Add error event listener for uncaught errors
  useEffect(() => {
    const handleError = (event: ErrorEvent) => {
      console.error('[Global Error]', {
        message: event.message,
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
        error: event.error,
        stack: event.error?.stack
      });
    };
    window.addEventListener('error', handleError);
    return () => window.removeEventListener('error', handleError);
  }, []);

  // Cloud sync hooks - initialize store listeners at top level
  useEPGStoreCloudSync();
  useTableColumnStoreCloudSync();
  useUIStoreCloudSync();

  // SSE notification hooks - listen for real-time events (Gmail polling postcards)
  // Only enable when authenticated to avoid unnecessary connections
  usePostcardSSENotifications(isAuthenticated());

  // Initialize cloud sync on mount - ONLY if authenticated
  useEffect(() => {
    async function loadCloudPreferences() {
      // CRITICAL FIX: Skip cloud sync if not authenticated
      // This prevents 401 errors and infinite token refresh loops on app load
      if (!isAuthenticated()) {
        logger.log('[App] Not authenticated - skipping cloud preferences load (will use localStorage)');
        return;
      }

      try {
        logger.log('[App] Loading cloud preferences from PostgreSQL...');
        // Load each preference independently to prevent one failure from blocking others
        const loadTasks = [
          initKanbanFromCloud().catch(err => logger.error('[App] Kanban preferences failed:', err)),
          initTableColumnsFromCloud().catch(err => logger.error('[App] Table columns preferences failed:', err)),
          initEPGViewFromCloud().catch(err => logger.error('[App] EPG view preferences failed:', err)),
          initUIFromCloud().catch(err => logger.error('[App] UI preferences failed:', err)),
        ];

        await Promise.allSettled(loadTasks);
        logger.log('[App] Cloud preferences load completed (with possible failures)');
      } catch (error) {
        logger.error('[App] Unexpected error loading cloud preferences:', error);
        // Don't block app startup - stores will use localStorage fallback
      }
    }

    loadCloudPreferences();
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      {children}
      {/* Toast notifications - mobile-first, bottom-positioned */}
      <ToastContainer />
      {/* DevTools only in development - automatically excluded from production builds */}
      {import.meta.env.DEV && (
        <>
          {/* React Query DevTools - hidden to reduce UI clutter
          <ReactQueryDevtools
            initialIsOpen={false}
            position="bottom"
            buttonPosition="bottom-right"
          />
          */}
          {/* FeatureFlagPanel - Removed to prevent production visibility issues */}
        </>
      )}
    </QueryClientProvider>
  );
};

export default Providers;
